import { useFilters } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { PITCH_VIS_FILTER_KEYS, PLAYER_PITCH_VIS_FILTER_SOURCE } from '@/analytics/snowplow/filterTracking';
import { useTrackFilterChanges } from '@/analytics/snowplow/hooks/useTrackFilterChanges';
import { unwrappedEventsDataDefaultColumnsAtom } from '@/atoms/dataLocker/events/table';
import { isMetricMappedAtom, metricFilterMappingEnabledAtom } from '@/atoms/metric/metricFilterMapping';
import { playerEventMetricsAtom } from '@/atoms/metric/playerMetrics';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { playerIdAtom } from '@/atoms/player/player';
import { playerEventMetricFiltersAtom, playerEventsForMetricCountAtom } from '@/atoms/queries/player/playerEventData';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { EventDataControls } from '@/components/controls/EventsDataControls';
import { PageTitle } from '@/components/head/PageTitle';
import { PlayerLayoutWithFilters } from '@/components/layout/player/PlayerLayoutWithFilters';
import { VisSectionContainer } from '@/components/layout/VisSectionContainer';
import { HydrateMetricFilterMapping } from '@/components/metric/HydrateMetricFilterMapping';
import { MetricKeyDropdown } from '@/components/metric/MetricKeyDropdown';
import { PlayerEventInsights } from '@/components/player/vis/PlayerEventInsights';
import { PlayerVisEventsTable } from '@/components/player/vis/PlayerVisEventsTable';
import { VisDisplaySection } from '@/components/section/VisDisplaySection';
import { PitchVisSettingsPanel } from '@/components/settings/PitchVisSettingsPanel';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { EventCountInfoBar } from '@/components/visualisations/EventCountInfoBar';
import { PitchVisualisation } from '@/components/visualisations/PitchVisualisation/PitchVisualisation';
import { EVENTS_TABLE_COLUMN_ENTITIES } from '@/consts/dataLocker/events';
import { playerPitchVisComponents } from '@/consts/visualisationComponents';
import { ALL_EVENTS_METRIC_KEY } from '@/consts/visualisations';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useApplyMetricFilterMapping } from '@/hooks/useApplyMetricFilterMapping/useApplyMetricFilterMapping';
import { useEntityPageTitle } from '@/hooks/useEntityPageTitle';
import { useInitPitchDisplayPreset } from '@/hooks/useInitPitchDisplayPreset/useInitPitchDisplayPreset';
import { useIsEventInsightsVisible } from '@/hooks/useIsEventInsightsVisible';
import { useMetricPitchPresetNavigation } from '@/hooks/useMetricPitchPresetNavigation/useMetricPitchPresetNavigation';
import { useTableColumns } from '@/hooks/useTableColumns';
import { visEventsUrl } from '@/query/url';
import { TableId } from '@/types/table';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const [{ metric: metricKey }] = useFilters('main');
  const playerId = useAtomValue(playerIdAtom);
  const playerEventMetricFilters = useAtomValue(playerEventMetricFiltersAtom);
  const isMetricMapped = useAtomValue(isMetricMappedAtom);
  useCsvDownload(
    pageTitle,
    visEventsUrl('player', playerId, metricKey, 'events', { ...playerEventMetricFilters }, isMetricMapped),
  );
  return null;
};

const VisDisplaySectionWithControls = ({ showInsights }: { showInsights: boolean }) => {
  useTableColumns(EVENTS_TABLE_COLUMN_ENTITIES);
  useApplyMetricFilterMapping();
  const [{ metric: selectedMetric }] = useFilters('main');
  const isMappingEnabled = useAtomValue(metricFilterMappingEnabledAtom);
  const pitchPresetRedirect = useInitPitchDisplayPreset();
  const onMetricChange = useMetricPitchPresetNavigation();

  if (pitchPresetRedirect) return pitchPresetRedirect;

  const metricKeyDropdownComponent = (
    <MetricKeyDropdown
      metricDataAtom={playerEventMetricsAtom}
      // the tab links drop the metric from the URL; with the mapping on, come back to the metric last selected
      initialMetricKey={(isMappingEnabled && selectedMetric) || ALL_EVENTS_METRIC_KEY}
      onMetricChange={onMetricChange}
    />
  );

  return (
    <VisSectionContainer>
      <EventDataControls metricKeyDropdownComponent={metricKeyDropdownComponent} />
      <VisDisplaySection
        rowBreakpoint="none"
        EventsTableComponent={PlayerVisEventsTable}
        visualisationComponent={
          <PitchVisualisation
            visualisations={playerPitchVisComponents}
            headerComponent={metricKeyDropdownComponent}
            footerComponent={<EventCountInfoBar eventCountAtom={playerEventsForMetricCountAtom} />}
          />
        }
        insightComponent={showInsights ? <PlayerEventInsights /> : undefined}
      />
    </VisSectionContainer>
  );
};

export const PlayerVisPage = () => {
  const { t } = useTranslation('navigation');
  const pageTitle = useEntityPageTitle(t('secondary.pitchVis'), useAtomValue(unwrappedSelectedPlayerNameAtom));
  useTrackFilterChanges(PITCH_VIS_FILTER_KEYS, PLAYER_PITCH_VIS_FILTER_SOURCE);
  const showInsights = useIsEventInsightsVisible();

  return (
    <ScopeProvider atoms={[tableColumnEntitiesAtom, metricFilterMappingEnabledAtom]}>
      <HydrateMetricFilterMapping>
        <PageTitle>{pageTitle}</PageTitle>
        <PlayerLayoutWithFilters
          settingsPanel={<PitchVisSettingsPanel />}
          insightComponent={showInsights ? <PlayerEventInsights /> : undefined}
        >
          <ScopedTableWithDefaultColumns
            defaultColumnsAtom={unwrappedEventsDataDefaultColumnsAtom}
            tableId={TableId.PLAYER_EVENTS}
          >
            <CsvDownload pageTitle={pageTitle} />
            <VisDisplaySectionWithControls showInsights={showInsights} />
          </ScopedTableWithDefaultColumns>
        </PlayerLayoutWithFilters>
      </HydrateMetricFilterMapping>
    </ScopeProvider>
  );
};
